import {
  isTransientLocalServiceError,
  LOCAL_PREVIEW_FETCH_RETRY_DELAYS_MS,
  sleepMs,
} from '../../net/transient-local-fetch-retry.js';
import { buildLocalProxyHttpRequest } from './build-local-proxy-http-request.js';
import { loadHttpModule, runLocalHttpRequestOnce } from './run-local-http-request.js';
import type { ProxyRequest, ProxyResponse } from './types.js';

function isIdempotentProxyMethod(method: string): boolean {
  const m = method.toUpperCase();
  return m === 'GET' || m === 'HEAD' || m === 'OPTIONS';
}

/** Proxy a single request to a local server. Only localhost is allowed. */
export async function proxyToLocal(request: ProxyRequest): Promise<ProxyResponse> {
  const built = buildLocalProxyHttpRequest(request);
  if ('error' in built) {
    return { id: request.id, statusCode: 0, headers: {}, body: '', error: built.error };
  }

  const isHttps = request.url.startsWith('https:');
  const mod = await loadHttpModule(isHttps);
  const maxAttempts =
    isIdempotentProxyMethod(request.method) ? LOCAL_PREVIEW_FETCH_RETRY_DELAYS_MS.length + 1 : 1;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const once = await runLocalHttpRequestOnce(mod, built);
    const errMsg = once.error ?? '';
    const canRetry =
      attempt < maxAttempts - 1 && errMsg !== '' && isTransientLocalServiceError(new Error(errMsg));
    if (!canRetry) {
      return {
        id: request.id,
        statusCode: once.statusCode,
        headers: once.headers,
        body: once.body.toString('utf8'),
        error: once.error,
      };
    }
    const waitMs =
      LOCAL_PREVIEW_FETCH_RETRY_DELAYS_MS[Math.min(attempt, LOCAL_PREVIEW_FETCH_RETRY_DELAYS_MS.length - 1)] ??
      100;
    await sleepMs(waitMs);
  }

  throw new Error('Local proxy retry loop exited unexpectedly');
}
